import liff from '@line/liff';

let initPromise: Promise<void> | null = null;

export function ensureLiffInit(): Promise<void> {
  if (!initPromise) {
    const liffId = import.meta.env.VITE_LIFF_ID as string | undefined;
    if (!liffId) {
      initPromise = Promise.reject(new Error('VITE_LIFF_ID not set'));
    } else {
      initPromise = liff.init({ liffId, withLoginOnExternalBrowser: false });
    }
  }
  return initPromise;
}

export async function getLiffIdToken(): Promise<string | null> {
  await ensureLiffInit();
  if (!liff.isLoggedIn()) {
    liff.login();
    return null;
  }
  return liff.getIDToken();
}

export function isInClient(): boolean {
  try {
    return liff.isInClient();
  } catch {
    return false;
  }
}

/**
 * Read the LIFF context — when LIFF is opened inside a LINE group/room chat,
 * `liff.getContext()` returns `{type: 'group', groupId: ...}` (or
 * `{type: 'room', roomId: ...}`). When opened outside LINE (PC web) or in 1:1
 * chat, returns `null`-ish for groupId so we treat it as personal.
 *
 * The result is stored in localStorage so subsequent navigations don't have to
 * re-init LIFF (slow).
 */
export type LiffChatContext =
  | { type: 'personal' }
  | { type: 'group'; lineGroupId: string; subType: 'group' | 'room' };

const CTX_KEY = 'billspilt:liff-ctx';

export async function readLiffChatContext(): Promise<LiffChatContext> {
  try {
    await ensureLiffInit();
    const ctx = liff.getContext();
    if (!ctx) return preserveOrPersonal();
    if (ctx.type === 'group' && ctx.groupId) {
      const out: LiffChatContext = {
        type: 'group',
        lineGroupId: ctx.groupId,
        subType: 'group',
      };
      cache(out);
      return out;
    }
    if (ctx.type === 'room' && ctx.roomId) {
      const out: LiffChatContext = {
        type: 'group',
        lineGroupId: ctx.roomId,
        subType: 'room',
      };
      cache(out);
      return out;
    }
    // ctx.type === 'utou' / 'external' / 'none' (post-login redirect frequently
    // surfaces 'none' temporarily). Don't blow away a previously-cached group
    // context — keep the last known good one.
    return preserveOrPersonal();
  } catch {
    return preserveOrPersonal();
  }
}

function preserveOrPersonal(): LiffChatContext {
  const prev = cachedLiffChatContext();
  if (prev.type === 'group') return prev;
  return personalCtx();
}

export function cachedLiffChatContext(): LiffChatContext {
  try {
    const raw = localStorage.getItem(CTX_KEY);
    if (raw) return JSON.parse(raw) as LiffChatContext;
  } catch {
    /* ignore */
  }
  return { type: 'personal' };
}

function personalCtx(): LiffChatContext {
  cache({ type: 'personal' });
  return { type: 'personal' };
}

function cache(c: LiffChatContext) {
  try {
    localStorage.setItem(CTX_KEY, JSON.stringify(c));
  } catch {
    /* ignore */
  }
}
